import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import type { FitCastBoardContext } from "@/lib/timeline/board/FitCast/context";
import type { PlaybackState } from "@/lib/timeline/playback";
import { frameLockedCamera } from "@/lib/timeline/board/FitCast/view";
import { cameraInsets, isPhoneStage } from "@/lib/timeline/view/CameraInsets/insets";
import { computeTimelineHomeView } from "@/lib/timeline/view/TimelineHomeView/home";
import { applyTimelineStageViewCss } from "@/lib/timeline/view/StageViewCss/css";
import { isRecoverableTimelineView } from "@/lib/timeline/view/TimelineViewRecovery/recovery";
import {
  easedLockedFrame,
  fitEaseDelta,
  stillCamera,
  type LockedViewVelocity,
} from "@/lib/timeline/view/SmoothLockedView/smooth";
import type { TimelineViewTransform } from "@/lib/timeline/view/ViewTransform/transform";

interface ResolveLockedViewInput {
  layout: BookTimelineLayout;
  stage: HTMLElement;
  getPlayback: () => PlaybackState;
  viewRef: { current: TimelineViewTransform };
  followZoomRef: { current: number };
  /** When true, locked camera auto-fits zoom; wheel zoom clears until lock toggles. */
  lockAutoFitRef: { current: boolean };
  lastFitMsRef: { current: number };
  getBoardFitContext?: () => FitCastBoardContext | null;
}

/** Each stage's camera keeps its speed from frame to frame. */
const velocities = new WeakMap<HTMLElement, LockedViewVelocity>();

function velocityOf(stage: HTMLElement): LockedViewVelocity {
  const known = velocities.get(stage);
  if (known !== undefined) {
    return known;
  }
  const fresh = stillCamera();
  velocities.set(stage, fresh);
  return fresh;
}

function measureOverlays(stage: HTMLElement, width: number) {
  const box = stage.getBoundingClientRect();
  if (!isPhoneStage(width, box.height)) {
    return undefined;
  }
  return {
    stageTop: box.top,
    stageBottom: box.bottom,
    captionBottom: stage.querySelector(".timeline-scene-overlay__text")
      ?.getBoundingClientRect().bottom,
    controlsTop: stage.querySelector(".timeline-transport")?.getBoundingClientRect().top,
  };
}

export function resolveLockedViewTransform(
  input: ResolveLockedViewInput,
): TimelineViewTransform {
  const w = Math.max(1, input.stage.clientWidth);
  const h = Math.max(1, input.stage.clientHeight);
  const heldZoom = input.lockAutoFitRef.current ? null : input.followZoomRef.current;
  const nowMs = performance.now();
  const insets = cameraInsets(w, h, measureOverlays(input.stage, w));
  const framed = frameLockedCamera(
    input.layout,
    w,
    h - insets.top - insets.bottom,
    w,
    input.getPlayback().playheadMs,
    heldZoom,
    input.getBoardFitContext?.() ?? null,
  );
  const fitted = { ...framed, panY: framed.panY + insets.top };
  let view = easedLockedFrame(
    input.viewRef.current,
    fitted,
    heldZoom,
    fitEaseDelta(nowMs, input.lastFitMsRef.current),
    w,
    h,
    velocityOf(input.stage),
  );
  if (!isRecoverableTimelineView(view)) {
    view = computeTimelineHomeView(
      input.layout,
      w,
      h,
      w,
      input.getPlayback().playheadMs,
    );
  }
  input.lastFitMsRef.current = nowMs;
  if (heldZoom === null) {
    input.followZoomRef.current = view.zoom;
  }
  input.viewRef.current = view;
  applyTimelineStageViewCss(input.stage, view);
  return view;
}
